import { splitHeadwordTemplate, segmentsToText, slotExample } from './headwordTemplate';

const CASE_VALUES = [
  'akkusativ',
  'dativ',
  'genitiv',
  'nominativ',
  'accusative',
  'dative',
  'genitive',
  'nominative',
  'akk',
  'dat',
  'gen',
  'nom',
];

const isCaseValue = (value) => {
  if (!value) return false;
  return CASE_VALUES.includes(String(value).toLowerCase());
};

const normalizeValencyObjectEntry = (entry) => {
  if (!entry) return { object: '', caseValue: '', example: '' };
  if (typeof entry === 'string') {
    return { object: entry, caseValue: '', example: '' };
  }
  if (typeof entry === 'object') {
    const objectValue = entry.object || entry.text || entry.value || entry.label || '';
    const caseValue = entry.case || entry.required_case || '';
    const exampleValue = entry.example || entry.object_example || '';
    return {
      object: objectValue,
      caseValue,
      example: exampleValue,
    };
  }
  return { object: String(entry), caseValue: '', example: '' };
};

const formatValencyObject = (obj, includeExamples = true) => {
  if (obj == null) return '';
  if (typeof obj === 'string') return obj;
  if (typeof obj === 'object') {
    const parts = [];
    if (obj.preposition) parts.push(obj.preposition);
    if (obj.case) parts.push(obj.case);
    if (includeExamples && obj.example) parts.push(obj.example);
    if (obj.object) parts.push(obj.object);
    if (parts.length) return parts.join(' · ');
  }
  return String(obj);
};

export const getHeaderLineItems = (
  word,
  {
    includeExamples = true,
    includeArticle = true,
    includeGerman = true,
  } = {}
) => {
  // Keep this in sync with backend/app/header_utils.py:build_wordcard_header_parts.
  const items = [];
  const grammar = word?.grammar_info || {};
  const type = word?.type || '';
  let german = (word?.german || '').trim();

  if (includeArticle && (type === 'Noun' || type === 'Noun with Preposition')) {
    let article = (grammar.article || '').trim();
    const germanLower = german.toLowerCase();
    if (article) {
      const prefix = `${article.toLowerCase()} `;
      if (germanLower.startsWith(prefix)) {
        german = german.slice(prefix.length).trim();
      }
    } else if (german) {
      const articlePrefix = ['der ', 'die ', 'das '].find((prefix) => germanLower.startsWith(prefix));
      if (articlePrefix) {
        article = german.slice(0, articlePrefix.length - 1);
        german = german.slice(articlePrefix.length).trim();
      }
    }
    if (article) {
      items.push({ type: 'article', article, gender: grammar.gender || '' });
    }
  }

  // Words with a headword template (grammar_info.headword, built by the
  // backend): slots, prepositions and the reflexive come from the tokens, in
  // template order. Slots inside the phrase stay in the `german` item as
  // segments; tokens after the last plain word become separate items.
  const template = typeof grammar.headword === 'string' ? grammar.headword.trim() : '';
  if (template) {
    const { inline, trailing } = splitHeadwordTemplate(template);
    if (includeGerman && inline.length) {
      const text = segmentsToText(inline);
      const hasTokens = inline.some((seg) => seg.type === 'token');
      items.push(hasTokens
        ? {
          type: 'german',
          text,
          segments: inline.map((seg) => (seg.type === 'token' && seg.tokenType === 'slot'
            ? { ...seg, example: includeExamples ? slotExample(grammar, seg.id) : '' }
            : seg)),
        }
        : { type: 'german', text });
    }
    trailing.forEach((seg) => {
      if (seg.tokenType === 'reflexive') {
        items.push({ type: 'reflexive', caseValue: seg.caseValue });
      } else if (seg.tokenType === 'preposition') {
        items.push({ type: 'preposition', preposition: seg.preposition, example: '' });
      } else if (seg.tokenType === 'slot') {
        items.push({
          type: 'slot',
          caseValue: seg.caseValue,
          abbreviation: seg.abbreviation,
          kind: seg.kind,
          example: includeExamples ? slotExample(grammar, seg.id) : '',
        });
      }
    });
    return items;
  }

  if (includeGerman && german) {
    items.push({ type: 'german', text: german });
  }

  if (grammar.reflexive_verb_case) {
    items.push({ type: 'reflexive', caseValue: grammar.reflexive_verb_case });
  }

  const objects = Array.isArray(grammar.valency?.objects) ? grammar.valency.objects : [];
  objects.forEach((obj) => {
    const normalized = normalizeValencyObjectEntry(obj);
    const caseValue = normalized.caseValue || (typeof obj === 'string' ? obj : '');
    if (isCaseValue(caseValue)) {
      items.push({
        type: 'object_case',
        caseValue,
        example: includeExamples ? normalized.example : '',
      });
      return;
    }

    const label = formatValencyObject(obj, includeExamples);
    if (label) {
      items.push({ type: 'object_label', label });
    }
  });

  const prepositionalObjects = Array.isArray(grammar.valency?.prepositional_objects)
    ? grammar.valency.prepositional_objects
    : [];

  if (prepositionalObjects.length > 0) {
    prepositionalObjects.forEach((prepObj) => {
      const preposition = (prepObj.preposition || '').trim();
      const example = includeExamples ? (prepObj.example || prepObj.object_example || '') : '';
      if (preposition && type !== 'Preposition') {
        items.push({
          type: 'preposition',
          preposition,
          example: prepObj.case ? '' : example,
        });
      }
      if (prepObj.case) {
        items.push({
          type: 'preposition_case',
          caseValue: prepObj.case,
          example,
        });
      } else if (!preposition && example) {
        items.push({
          type: 'preposition_example',
          example,
        });
      }
    });
  } else {
    const preposition = (grammar.preposition || '').trim();
    const example = includeExamples ? (grammar.case_object_example || '') : '';
    if (preposition && type !== 'Preposition') {
      items.push({
        type: 'preposition',
        preposition,
        example: grammar.required_case ? '' : example,
      });
    }
    if (grammar.required_case) {
      items.push({
        type: 'preposition_case',
        caseValue: grammar.required_case,
        example,
      });
    } else if (!preposition && example) {
      items.push({
        type: 'preposition_example',
        example,
      });
    }
  }

  return items;
};

/**
 * Head text for views that draw the headword themselves (wallpaper):
 * the part of the template inside the phrase, with slot abbreviations.
 * Returns null for words without a template.
 */
export const getHeadwordInline = (word) => {
  const template = word?.grammar_info?.headword;
  if (typeof template !== 'string' || !template.trim()) return null;
  const { inline } = splitHeadwordTemplate(template);
  return { text: segmentsToText(inline), segments: inline };
};

export const getHeaderLineText = (word) => {
  const items = getHeaderLineItems(word, { includeExamples: false });
  return items
    .map((item) => {
      switch (item.type) {
        case 'article': return item.article;
        case 'german': return item.text;
        case 'reflexive': return item.caseValue;
        case 'object_case': return item.caseValue;
        case 'slot': return item.abbreviation;
        case 'object_label': return item.label;
        case 'preposition': return item.preposition;
        case 'preposition_case': return item.caseValue;
        default: return '';
      }
    })
    .filter(Boolean)
    .join(' ');
};

export const getFormsLineText = (word) => {
  const type = (word?.type || '').toLowerCase();
  const grammar = word?.grammar_info || {};

  if (type.includes('verb')) {
    const forms = grammar.verb_forms || {};
    const parts = [forms.present_3rd_singular, forms.past_perfect, forms.praeteritum]
      .map((value) => (value || '').trim())
      .filter(Boolean);
    return parts.length ? parts.join(' · ') : '';
  }

  if (type.includes('adjective')) {
    const forms = grammar.adjective_forms || {};
    const antonym = (forms.antonym || '').trim();
    const adjectiveParts = [forms.comparative, forms.superlative]
      .map((value) => (value || '').trim())
      .filter(Boolean);
    const adjectiveText = adjectiveParts.length ? adjectiveParts.join(' · ') : '';
    if (antonym && adjectiveText) {
      return `${antonym} | ${adjectiveText}`;
    }
    return antonym || adjectiveText;
  }

  if (type.includes('noun')) {
    const plural = (grammar.plural || '').trim();
    return plural;
  }

  return '';
};
